import React, { useState, useEffect, useRef } from 'react';
import { Upload, Button, Input, message, Card, Row, Col, Image, Space, Progress, List, Modal, Popconfirm, Checkbox, Pagination } from 'antd';
import { 
  PictureOutlined,
  VideoCameraOutlined, 
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  UploadOutlined,
  UserOutlined,
  CheckCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import './index.css';

const { Dragger } = Upload;
const { TextArea } = Input;

const API_BASE = '/api';

function Gen2() {
  const [avatarImage, setAvatarImage] = useState(null);  // 形象图
  const [productImage, setProductImage] = useState(null);  // 产品图
  const [tryonImage, setTryonImage] = useState(null);  // 试穿图
  const [motionVideo, setMotionVideo] = useState(null);  // 动作视频
  const [finalVideo, setFinalVideo] = useState(null);  // 最终视频
  const [history, setHistory] = useState([]);  // 历史记录（从后端分页加载）
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingMotion, setUploadingMotion] = useState(false);
  const [generatingTryon, setGeneratingTryon] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [limits, setLimits] = useState(null);  // 用户次数限制
  
  // 加载用户次数
  const loadLimits = async () => {
    try {
      const res = await axios.get(`${API_BASE}/auth/me/limits`);
      if (res.data.success) {
        setLimits(res.data.limits);
      }
    } catch (err) {
      console.error('加载次数限制失败', err);
    }
  };
  
  // 计算剩余次数
  const getRemaining = (type) => {
    if (!limits) return null;
    const limit = limits[`${type}_limit`];
    const used = limits[`${type}_used`];
    if (limit === -1) return -1; // 无限
    return Math.max(0, limit - used);
  };
  
  // 抖音链接相关
  const [douyinUrl, setDouyinUrl] = useState('');
  const [fetchingDouyin, setFetchingDouyin] = useState(false);
  const [pendingDouyinImage, setPendingDouyinImage] = useState(null); // 已抓取，待确认的商品主图
  
  // 历史图片列表
  const [avatarList, setAvatarList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [showProductList, setShowProductList] = useState(false);
  // 动作视频管理弹窗
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);  // 预览视频
  const [historyPreviewVideo, setHistoryPreviewVideo] = useState(null);  // 历史记录预览
  const [videoList, setVideoList] = useState([]);  // 动作视频列表

  // 随机动作视频
  const [randomMotion, setRandomMotion] = useState(false);
  const [tryonList, setTryonList] = useState([]);
  const [showTryonList, setShowTryonList] = useState(false);
  
  // 形象图管理弹窗
  const [avatarModalVisible, setAvatarModalVisible] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  
  // 随机形象
  const [randomAvatar, setRandomAvatar] = useState(false);
  
  // URL参数
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');
  
  // 加载历史资源
  const loadHistoryImages = async () => {
    try {
      const [avatarsRes, productsRes, videosRes, tryonRes] = await Promise.all([
        axios.get(`${API_BASE}/avatars`),
        axios.get(`${API_BASE}/images`),
        axios.get(`${API_BASE}/videos?type=motion`),
        axios.get(`${API_BASE}/images/tryon`)
      ]);
      setAvatarList(avatarsRes.data.data?.avatars || []);
      setProductList(productsRes.data.files || []);
      setVideoList(videosRes.data.files || []);
      setTryonList(tryonRes.data.files || []);
    } catch (err) {
      console.error('加载历史资源失败', err);
    }
  };
  
  // 根据商品ID加载商品主图
  const loadProductImage = async () => {
    if (!productId) return;
    try {
      const res = await axios.get(`${API_BASE}/products/${productId}`);
      if (res.data.success && res.data.data) {
        const product = res.data.data;
        // 设置商品图片，使用存储的image_url
        setProductImage({
          name: product.title || '商品图',
          path: product.image_path,
          url: product.image_url || `/workspace/products/${product.image_path.split('/').pop()}`
        });
        message.success(`已加载商品: ${product.title || '商品主图'}`);
      }
    } catch (err) {
      console.error('加载商品图片失败', err);
      message.error('加载商品图片失败');
    }
  };
  
  useEffect(() => {
    loadHistoryImages();
    loadHistory(1, historyPageSize);
    loadLimits();
  }, []);

  useEffect(() => {
    if (productId) {
      loadProductImage();
    }
  }, [productId]);

  // 加载历史记录
  const loadHistory = async (page = historyPage, pageSize = historyPageSize) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/history/gen2`, {
        params: { page, pageSize }
      });
      if (res.data.success) {
        setHistory(res.data.data || []);
        setHistoryPage(res.data.pagination?.page || page);
        setHistoryPageSize(res.data.pagination?.pageSize || pageSize);
        setHistoryTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('加载历史记录失败', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 上传形象图
  const uploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    setUploadingAvatar(true);
    try {
      const res = await axios.post(`${API_BASE}/avatars`, formData);
      if (res.data.success) {
        const avatar = res.data.avatar;
        setAvatarImage({
          id: avatar.id,
          path: avatar.path,
          url: `${API_BASE}/avatars/${avatar.id}/image`
        });
        message.success('形象图上传成功');
      }
    } catch (err) {
      message.error('上传失败');
    } finally {
      setUploadingAvatar(false);
    }
    return false;
  };

  // 管理弹窗内上传形象图（支持批量）
  const uploadAvatarInModal = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const res = await axios.post(`${API_BASE}/avatars`, formData);
      return res.data.success;
    } catch (err) {
      message.error('上传失败');
      return false;
    }
  };

  // 批量上传形象图
  const avatarBatchUploadingRef = useRef(false);
  const uploadAvatarBatch = async (files) => {
    if (files.length === 0) return;
    // 防止重复调用：multiple模式下beforeUpload会对每个文件调用一次，用ref同步拦住
    if (avatarBatchUploadingRef.current) return;
    avatarBatchUploadingRef.current = true;
    setAvatarUploading(true);
    let successCount = 0;
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('avatar', file);
        const res = await axios.post(`${API_BASE}/avatars`, formData);
        if (res.data.success) {
          successCount++;
        }
      }
      // 刷新列表
      const avatarsRes = await axios.get(`${API_BASE}/avatars`);
      setAvatarList(avatarsRes.data.data?.avatars || []);
      if (successCount > 0) {
        message.success(`成功上传 ${successCount} 张形象图`);
      }
    } catch (err) {
      message.error('批量上传失败');
    } finally {
      setAvatarUploading(false);
      avatarBatchUploadingRef.current = false;
    }
  };

  // 删除形象图
  const deleteAvatar = async (avatarId) => {
    try {
      await axios.delete(`${API_BASE}/avatars/${avatarId}`);
      // 刷新列表
      const avatarsRes = await axios.get(`${API_BASE}/avatars`);
      setAvatarList(avatarsRes.data.data?.avatars || []);
      // 如果删除的是当前选中的，清除选中
      if (avatarImage && avatarImage.id === avatarId) {
        setAvatarImage(null);
      }
      message.success('形象图已删除');
    } catch (err) {
      message.error('删除失败');
    }
  };

  // 选择形象图（从管理弹窗）
  const selectAvatar = (av) => {
    setAvatarImage({ id: av.id, path: av.path, url: `${API_BASE}/avatars/${av.id}/image` });
    setAvatarModalVisible(false);
  };

  // 从抖音链接获取商品主图
  const fetchDouyinImage = async () => {
    if (!douyinUrl.trim()) {
      message.warning('请输入抖音商品链接');
      return;
    }
    setFetchingDouyin(true);
    setPendingDouyinImage(null);
    try {
      const res = await axios.post(`${API_BASE}/images/fetch-douyin`, { url: douyinUrl.trim() });
      if (res.data.success) {
        const img = res.data.image || res.data.file;
        setPendingDouyinImage({
          id: img.id,
          name: img.name,
          path: img.path,
          url: img.url,
          sourceUrl: img.sourceUrl
        });
        await loadHistoryImages();
        message.success('商品主图获取成功，请确认预览图');
      }
    } catch (err) {
      message.error('获取失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setFetchingDouyin(false);
    }
  };

  const confirmDouyinImage = () => {
    if (!pendingDouyinImage) return;
    setProductImage(pendingDouyinImage);
    setPendingDouyinImage(null);
    setDouyinUrl('');
    message.success('已确认商品主图');
  };

  const cancelDouyinImage = () => {
    setPendingDouyinImage(null);
  };

  // 生成试穿图
  const generateTryon = async () => {
    if (!productImage) {
      message.warning('请上传产品图'); return;
    }
    if (randomAvatar) {
      if (avatarList.length === 0) {
        message.warning('随机模式需要先在形象管理中添加形象图'); return;
      }
    } else if (!avatarImage) {
      message.warning('请上传形象图'); return;
    }

    setGeneratingTryon(true);
    try {
      // 随机模式：随机选一张形象图
      let selectedAvatar = avatarImage;
      if (randomAvatar && avatarList.length > 0) {
        const randomIndex = Math.floor(Math.random() * avatarList.length);
        const randomAv = avatarList[randomIndex];
        selectedAvatar = {
          id: randomAv.id,
          path: randomAv.path,
          url: `${API_BASE}/avatars/${randomAv.id}/image`
        };
        message.info(`已随机选择形象: ${randomAv.name || randomAv.id}`);
      }
      
      const res = await axios.post(`${API_BASE}/gen2/generate-tryon`, {
        productImagePath: productImage.path,
        avatarImagePath: selectedAvatar.path
      });

      if (res.data.success) {
        setTryonImage({
          path: res.data.imagePath,
          url: res.data.imageUrl
        });
        message.success('试穿图生成成功');
        loadLimits(); // 刷新次数
      } else {
        message.error('生成失败: ' + res.data.error);
      }
    } catch (err) {
      message.error('生成失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingTryon(false);
    }
  };

  // 上传动作视频
  const uploadMotionVideo = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    setUploadingMotion(true);
    try {
      const res = await axios.post(`${API_BASE}/videos/upload`, formData);
      if (res.data.success) {
        setMotionVideo({
          name: res.data.file.name,
          path: res.data.file.path,
          url: res.data.file.url,
          thumbnailUrl: res.data.file.thumbnailUrl
        });
        message.success('动作视频上传成功');
      }
    } catch (err) {
      message.error('上传失败');
    } finally {
      setUploadingMotion(false);
    }
    return false;
  };

  // 管理弹窗内上传动作视频
  const uploadVideoInModal = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    setVideoUploading(true);
    try {
      const res = await axios.post(`${API_BASE}/videos/upload`, formData);
      if (res.data.success) {
        // 刷新列表（只加载用户上传的动作视频）
        const videosRes = await axios.get(`${API_BASE}/videos?type=motion`);
        setVideoList(videosRes.data.files || []);
        message.success('动作视频上传成功');
      }
    } catch (err) {
      message.error('上传失败');
    } finally {
      setVideoUploading(false);
    }
    return false;
  };

  // 删除动作视频
  const deleteVideo = async (videoName) => {
    try {
      await axios.delete(`${API_BASE}/videos/${videoName}`);
      // 刷新列表（只加载用户上传的动作视频）
      const videosRes = await axios.get(`${API_BASE}/videos?type=motion`);
      setVideoList(videosRes.data.files || []);
      // 如果删除的是当前选中的，清除选中
      if (motionVideo && motionVideo.name === videoName) {
        setMotionVideo(null);
      }
      message.success('动作视频已删除');
    } catch (err) {
      message.error('删除失败');
    }
  };

  // 选择动作视频（从管理弹窗）
  const selectVideo = (vid) => {
    setMotionVideo({ name: vid.name, path: vid.path, url: vid.url, thumbnailUrl: vid.thumbnailUrl });
    setVideoModalVisible(false);
  };

  // 生成动作视频
  const generateMotionVideo = async () => {
    if (!tryonImage) {
      message.warning('请先生成试穿图'); return;
    }
    if (randomMotion) {
      if (videoList.length === 0) {
        message.warning('随机模式需要先在动作视频管理中添加视频'); return;
      }
    } else if (!motionVideo) {
      message.warning('请上传动作视频'); return;
    }
    if (generatingVideo) {
      return; // 防止重复点击
    }

    setGeneratingVideo(true);
    setVideoProgress(0);
    
    // 模拟进度
    const progressTimer = setInterval(() => {
      setVideoProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressTimer);
          return prev;
        }
        return prev + 10;
      });
    }, 3000);

    try {
      // 随机模式：随机选一个动作视频
      let selectedVideo = motionVideo;
      if (randomMotion && videoList.length > 0) {
        const randomIndex = Math.floor(Math.random() * videoList.length);
        const randomVid = videoList[randomIndex];
        selectedVideo = {
          name: randomVid.name,
          path: randomVid.path,
          url: randomVid.url,
          thumbnailUrl: randomVid.thumbnailUrl
        };
        message.info(`已随机选择动作视频: ${randomVid.name}`);
      }
      
      const res = await axios.post(`${API_BASE}/gen2/generate-video`, {
        tryonImagePath: tryonImage.path,
        motionVideoPath: selectedVideo.path,
        productId: productId ? parseInt(productId) : null
      });

      clearInterval(progressTimer);
      setVideoProgress(100);

      if (res.data.success) {
        const newVideo = {
          path: res.data.videoPath,
          url: res.data.videoUrl,
          createdAt: new Date().toLocaleString('zh-CN'),
          tryonImage: tryonImage.url,
          motionVideo: selectedVideo.url
        };
        setFinalVideo(newVideo);
        
        // 重新加载历史记录（后端已自动保存）
        await loadHistory(1, historyPageSize);
        
        message.success('视频生成成功');
        loadLimits(); // 刷新次数
      } else {
        message.error('生成失败: ' + res.data.error);
      }
    } catch (err) {
      clearInterval(progressTimer);
      message.error('生成失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingVideo(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="page-toolbar">
        <div>
          <div className="page-kicker">生成中心</div>
          <h1><VideoCameraOutlined /> 动作模仿视频</h1>
        </div>
      </div>
      
      {/* 次数提示 */}
      {limits && (
        <div style={{ marginBottom: 12, padding: '8px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <span>生图剩余: <strong style={{ color: getRemaining('image') === 0 ? '#ff4d4f' : '#52c41a' }}>
            {getRemaining('image') === -1 ? '无限' : getRemaining('image')}
          </strong></span>
          <span>生视频剩余: <strong style={{ color: getRemaining('video') === 0 ? '#ff4d4f' : '#52c41a' }}>
            {getRemaining('video') === -1 ? '无限' : getRemaining('video')}
          </strong></span>
        </div>
      )}
      
      <div className="content-stack">
        {/* 图片上传区域 - 平铺 */}
        <div style={{ display: 'flex', gap: 16, marginTop: 20, marginBottom: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* 形象图 */}
          <div style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 250 }}>
            <Card 
              title="形象图（人物参考）"
            >
              {avatarImage ? (
                <div style={{ textAlign: 'center' }}>
                  <Image 
                    src={avatarImage.url} 
                    style={{ maxHeight: 200, objectFit: 'contain' }} 
                  />
                  <Button 
                    danger 
                    size="small" 
                    icon={<DeleteOutlined />}
                    onClick={() => setAvatarImage(null)}
                    style={{ marginTop: 8 }}
                  >
                    清除
                  </Button>
                </div>
              ) : (
                <>
                <Dragger 
                  showUploadList={false} 
                  beforeUpload={uploadAvatar} 
                  accept="image/*"
                  loading={uploadingAvatar}
                >
                  <p style={{ fontSize: 48, marginBottom: 16 }}><UserOutlined /></p>
                  <p>点击上传形象图</p>
                </Dragger>
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Button size="small" icon={<SettingOutlined />} onClick={() => {
                    setAvatarModalVisible(true);
                  }}>
                    管理形象 {avatarList.length > 0 && `(${avatarList.length})`}
                  </Button>
                </div>
                </>
              )}
            </Card>
          </div>

          {/* 产品图 */}
          <div style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 250 }}>
            <Card 
              title="产品图（服装参考）"
            >
              {productImage ? (
                <div style={{ textAlign: 'center' }}>
                  <Image 
                    src={productImage.url} 
                    style={{ maxHeight: 200, objectFit: 'contain' }} 
                  />
                  <Button 
                    danger 
                    size="small" 
                    icon={<DeleteOutlined />}
                    onClick={() => setProductImage(null)}
                    style={{ marginTop: 8 }}
                  >
                    清除
                  </Button>
                </div>
              ) : (
                <>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <Input.Search
                    placeholder="粘贴抖音商品链接后按回车"
                    value={douyinUrl}
                    onChange={e => setDouyinUrl(e.target.value)}
                    onSearch={fetchDouyinImage}
                    loading={fetchingDouyin}
                    enterButton="获取主图"
                    size="middle"
                  />
                </div>
                <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
                  输入抖音商品链接，获取商品主图，预览确认后作为产品图
                </div>
                {pendingDouyinImage && (
                  <div style={{ textAlign: 'center', marginTop: 12, padding: 8, border: '1px solid #eee', borderRadius: 8 }}>
                    <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>请确认抓取到的商品主图</div>
                    <Image
                      src={pendingDouyinImage.url}
                      style={{ maxHeight: 180, objectFit: 'contain' }}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Space>
                        <Button type="primary" size="small" icon={<CheckCircleOutlined />} onClick={confirmDouyinImage}>
                          确认使用
                        </Button>
                        <Button size="small" onClick={cancelDouyinImage}>
                          取消
                        </Button>
                      </Space>
                    </div>
                  </div>
                )}
                {productList.length > 0 && (
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Button size="small" onClick={() => setShowProductList(!showProductList)}>
                      {showProductList ? '收起' : '选择已有产品图'} ({productList.length})
                    </Button>
                  </div>
                )}
                {showProductList && (
                  <div style={{ marginTop: 8, maxHeight: 150, overflowY: 'auto' }}>
                    {productList.map(img => (
                      <div key={img.name} style={{ display: 'inline-block', margin: 4, cursor: 'pointer' }} onClick={() => {
                        setProductImage({ name: img.name, path: img.path, url: img.url });
                        setShowProductList(false);
                      }}>
                        <img src={img.url} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                      </div>
                    ))}
                  </div>
                )}
                </>
              )}
            </Card>
          </div>

          {/* 试穿图预览 */}
          <div style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 250 }}>
            <Card 
              title="生成的试穿图"
            >
              {tryonImage ? (
                <div style={{ textAlign: 'center' }}>
                  <Image 
                    src={tryonImage.url} 
                    style={{ maxHeight: 200, objectFit: 'contain' }} 
                    preview 
                  />
                </div>
              ) : (
                <>
                <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                  等待生成试穿图...
                </div>
                {tryonList.length > 0 && (
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Button size="small" onClick={() => setShowTryonList(!showTryonList)}>
                      {showTryonList ? '收起' : '选择已有试穿图'} ({tryonList.length})
                    </Button>
                  </div>
                )}
                {showTryonList && (
                  <div style={{ marginTop: 8, maxHeight: 150, overflowY: 'auto' }}>
                    {tryonList.map(img => (
                      <div key={img.name} style={{ display: 'inline-block', margin: 4, cursor: 'pointer' }} onClick={() => {
                        setTryonImage({ name: img.name, path: img.path, url: img.url });
                        setShowTryonList(false);
                      }}>
                        <img src={img.url} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                      </div>
                    ))}
                  </div>
                )}
                </>
              )}
            </Card>
          </div>
        </div>

        {/* 生成试穿图按钮 */}
        <Card 
          style={{ marginBottom: 20, textAlign: 'center' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Button
              type="primary"
              size="large"
              icon={<PictureOutlined />}
              onClick={generateTryon}
              loading={generatingTryon}
              disabled={(!avatarImage && !randomAvatar) || !productImage || getRemaining('image') === 0}
            >
              生成试穿图
            </Button>
            <Checkbox 
              checked={randomAvatar} 
              onChange={(e) => setRandomAvatar(e.target.checked)}
            >
              随机形象（当前 {avatarList.length} 张）
            </Checkbox>
          </div>
        </Card>

        {/* 动作视频上传 */}
        <Card 
          title="动作视频（.mp4）"
          style={{ marginBottom: 20 }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 400px', minWidth: 300 }}>
              {motionVideo ? (
                <div>
                  <video 
                    src={motionVideo.url} 
                    controls 
                    style={{ maxHeight: 200, maxWidth: '100%' }}
                    type="video/mp4"
                    preload="metadata"
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                    文件: {motionVideo.name} | 
                    <a href={motionVideo.url} target="_blank" rel="noopener noreferrer">新窗口打开</a>
                  </div>
                  <Button 
                    danger 
                    size="small" 
                    icon={<DeleteOutlined />}
                    onClick={() => setMotionVideo(null)}
                    style={{ marginTop: 8 }}
                  >
                    清除
                  </Button>
                </div>
              ) : (
                <>
                <Dragger 
                  showUploadList={false} 
                  beforeUpload={uploadMotionVideo} 
                  accept="video/mp4,video/*"
                  loading={uploadingMotion}
                >
                  <p style={{ fontSize: 48, marginBottom: 16 }}><VideoCameraOutlined /></p>
                  <p>点击上传动作视频（.mp4）</p>
                </Dragger>
                {videoList.length > 0 && (
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Button size="small" icon={<SettingOutlined />} onClick={() => {
                      setVideoModalVisible(true);
                    }}>
                      管理动作视频 ({videoList.length})
                    </Button>
                  </div>
                )}
                </>
              )}
            </div>
            <div style={{ flex: '0 0 auto' }}>
              {generatingVideo && (
                <div>
                  <p>视频生成中...</p>
                  <Progress percent={videoProgress} status="active" />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* 生成视频按钮 */}
        <Card 
          style={{ marginBottom: 20, textAlign: 'center' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Button
              type="primary"
              size="large"
              icon={<VideoCameraOutlined />}
              onClick={generateMotionVideo}
              loading={generatingVideo}
              disabled={(!motionVideo && !randomMotion) || !tryonImage || generatingVideo || getRemaining('video') === 0}
            >
              生成动作模仿视频
            </Button>
            <Checkbox 
              checked={randomMotion} 
              onChange={(e) => setRandomMotion(e.target.checked)}
            >
              随机动作视频（当前 {videoList.length} 个）
            </Checkbox>
          </div>
        </Card>

        {/* 最终视频 */}
        {finalVideo && (
          <Card 
            title="生成的视频"
            style={{ marginBottom: 20 }}
          >
            <video 
              src={finalVideo.url} 
              controls 
              style={{ maxWidth: 400, width: '100%' }} 
            />
            <div style={{ marginTop: 16 }}>
              <Space>
                <Button 
                  icon={<PlayCircleOutlined />}
                  onClick={() => window.open(finalVideo.url, '_blank')}
                >
                  新窗口播放
                </Button>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(finalVideo.url, '_blank')}
                >
                  下载
                </Button>
              </Space>
            </div>
          </Card>
        )}

        {/* 历史记录 */}
        {(history.length > 0 || historyLoading || historyTotal > 0) && (
          <Card 
            title={`历史记录${historyTotal > 0 ? `（共 ${historyTotal} 条）` : ''}`}
            style={{ marginBottom: 20 }}
          >
            <List
              loading={historyLoading}
              dataSource={history}
              renderItem={(item, index) => (
                <List.Item
                  actions={[
                    <Button key="play" icon={<PlayCircleOutlined />} onClick={() => setHistoryPreviewVideo(item)}>播放</Button>,
                    <Button key="download" icon={<DownloadOutlined />} onClick={() => window.open(item.videoUrl, '_blank')}>下载</Button>
                  ]}
                >
                  <List.Item.Meta
                    title={`视频 ${historyTotal - ((historyPage - 1) * historyPageSize + index)}`}
                    description={item.createdAt}
                    avatar={
                      <div 
                        style={{ width: 80, height: 60, cursor: 'pointer', position: 'relative', borderRadius: 4, overflow: 'hidden' }}
                        onClick={() => setHistoryPreviewVideo(item)}
                      >
                        <img 
                          src={item.videoUrl ? item.videoUrl.replace('/videos/', '/thumbnails/').replace('.mp4', '_thumb.jpg') : ''} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          width: 30,
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PlayCircleOutlined style={{ color: '#fff', fontSize: 16 }} />
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            {historyTotal > historyPageSize && (
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Pagination
                  current={historyPage}
                  pageSize={historyPageSize}
                  total={historyTotal}
                  showSizeChanger
                  showTotal={(total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`}
                  pageSizeOptions={['5', '10', '20', '50']}
                  onChange={(page, pageSize) => loadHistory(page, pageSize)}
                  onShowSizeChange={(current, size) => loadHistory(1, size)}
                />
              </div>
            )}
          </Card>
        )}

        {/* 历史视频播放弹窗 */}
        <Modal
          title="视频播放"
          open={!!historyPreviewVideo}
          onCancel={() => setHistoryPreviewVideo(null)}
          footer={null}
          width={600}
        >
          {historyPreviewVideo && (
            <video 
              src={historyPreviewVideo.videoUrl} 
              controls 
              style={{ width: '100%', borderRadius: 8 }}
              autoPlay
            />
          )}
        </Modal>

        {/* 形象图管理弹窗 */}
        <Modal
          title="管理形象图"
          open={avatarModalVisible}
          onCancel={() => setAvatarModalVisible(false)}
          footer={null}
          width={600}
        >
          <div style={{ marginBottom: 16 }}>
            <Dragger
              showUploadList={false}
              beforeUpload={(file, fileList) => {
                // 批量上传
                uploadAvatarBatch(fileList);
                return false; // 阻止默认上传
              }}
              accept="image/*"
              loading={avatarUploading}
              multiple
            >
              <p style={{ fontSize: 32, marginBottom: 8 }}><UserOutlined /></p>
              <p>点击或拖拽上传形象图（支持批量）</p>
            </Dragger>
          </div>
          {avatarList.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 400, overflowY: 'auto' }}>
              {avatarList.map(av => (
                <div 
                  key={av.id} 
                  style={{ 
                    position: 'relative',
                    width: 120,
                    height: 120,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: avatarImage && avatarImage.id === av.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    cursor: 'pointer'
                  }}
                  onClick={() => selectAvatar(av)}
                >
                  <img 
                    src={`${API_BASE}/avatars/${av.id}/image`} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                  {avatarImage && avatarImage.id === av.id && (
                    <div style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: '#1890ff',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircleOutlined style={{ color: '#fff', fontSize: 12 }} />
                    </div>
                  )}
                  <Popconfirm
                    title="确定删除此形象图？"
                    onConfirm={(e) => {
                      e.stopPropagation();
                      deleteAvatar(av.id);
                    }}
                    okText="确认"
                    cancelText="取消"
                  >
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        background: 'rgba(0,0,0,0.5)',
                        borderRadius: 4,
                        padding: '2px 6px',
                        cursor: 'pointer'
                      }}
                    >
                      <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                    </div>
                  </Popconfirm>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
              暂无形象图，请上传
            </div>
          )}
        </Modal>

        {/* 动作视频管理弹窗 */}
        <Modal
          title="管理动作视频"
          open={videoModalVisible}
          onCancel={() => { setVideoModalVisible(false); setPreviewVideo(null); }}
          footer={null}
          width={700}
        >
          {/* 预览区域 */}
          {previewVideo && (
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <video 
                src={previewVideo.url} 
                controls 
                style={{ maxHeight: 250, maxWidth: '100%', borderRadius: 8 }}
                type="video/mp4"
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {previewVideo.name}
              </div>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <Dragger
              showUploadList={false}
              beforeUpload={uploadVideoInModal}
              accept="video/mp4,video/*"
              loading={videoUploading}
            >
              <p style={{ fontSize: 32, marginBottom: 8 }}><VideoCameraOutlined /></p>
              <p>点击上传新动作视频（.mp4）</p>
            </Dragger>
          </div>
          {videoList.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 400, overflowY: 'auto' }}>
              {videoList.map(vid => (
                <div 
                  key={vid.name} 
                  style={{ 
                    position: 'relative',
                    width: 120,
                    height: 120,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: motionVideo && motionVideo.name === vid.name ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    cursor: 'pointer'
                  }}
                  onClick={() => selectVideo(vid)}
                >
                  {vid.thumbnailUrl ? (
                    <img 
                      src={vid.thumbnailUrl} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <video 
                      src={vid.url} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                  {/* 播放图标 */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PlayCircleOutlined style={{ color: '#fff', fontSize: 20 }} />
                  </div>
                  {/* 选中标记 */}
                  {motionVideo && motionVideo.name === vid.name && (
                    <div style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: '#1890ff',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircleOutlined style={{ color: '#fff', fontSize: 12 }} />
                    </div>
                  )}
                  {/* 预览按钮 */}
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewVideo(vid);
                    }}
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      left: 4,
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: 4,
                      padding: '2px 6px',
                      cursor: 'pointer'
                    }}
                  >
                    <PlayCircleOutlined style={{ color: '#fff', fontSize: 12 }} />
                  </div>
                  {/* 删除按钮 */}
                  <Popconfirm
                    title="确定删除此动作视频？"
                    onConfirm={(e) => {
                      e.stopPropagation();
                      deleteVideo(vid.name);
                    }}
                    okText="确认"
                    cancelText="取消"
                  >
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        background: 'rgba(0,0,0,0.5)',
                        borderRadius: 4,
                        padding: '2px 6px',
                        cursor: 'pointer'
                      }}
                    >
                      <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                    </div>
                  </Popconfirm>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
              暂无动作视频，请上传
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}

export default Gen2;
